import { useRef, useEffect } from "react";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { shakeState } from "../../systems/shakeState";
import { warpState } from "../../systems/warpState";
import { cameraState } from "../../systems/cameraState";
import { createPlanetMesh, defaultVisuals } from "../../systems/PlanetGenerator";
import type { AlertLevel, SpatialState, SpatialEntity } from "../../types/game";
import type { SceneCommandExecutor, ActiveEffect } from "../../systems/SceneCommandExecutor";

interface Props {
  alertLevel: AlertLevel;
  executor: SceneCommandExecutor | null;
  onReady?: () => void;
  /** Replay mode: external spatial state (bypasses executor) */
  replaySpatial?: SpatialState | null;
  /** Replay mode: external effects list */
  replayEffects?: ActiveEffect[];
  /** Replay mode: camera override called each frame instead of normal camera logic */
  cameraOverride?: (camera: THREE.PerspectiveCamera, delta: number) => void;
}

const ALERT_COLORS: Record<AlertLevel, { ambient: number; dir: number }> = {
  normal: { ambient: 0x1a2a4a, dir: 0x667799 },
  yellow: { ambient: 0x4a3a1a, dir: 0x998866 },
  red:    { ambient: 0x4a1a1a, dir: 0x996666 },
};

const TRAIL_LENGTH = 40;
const TRAIL_UPDATE_FRAMES = 4;

// ── Entity mesh factory ──

function createEntityMesh(entity: SpatialEntity): THREE.Group {
  const { type, faction } = entity;

  if (type === "planet") {
    const visuals = entity.planetVisuals ?? defaultVisuals();
    return createPlanetMesh(entity.id, visuals);
  }

  const group = new THREE.Group();

  if (type === "ship") {
    const isVelan = faction === "Velan Dominion";

    if (isVelan) {
      // Velan — angular predatory cruiser
      // Main hull — elongated wedge
      const hull = new THREE.Mesh(
        new THREE.ConeGeometry(3.6, 14, 4),
        new THREE.MeshStandardMaterial({ color: 0x7a1515, metalness: 0.7, roughness: 0.25, emissive: 0x220000 })
      );
      hull.rotation.z = Math.PI / 2;
      group.add(hull);

      // Wing struts
      for (const [side, zOff] of [[1, 1.8], [-1, -1.8]] as [number, number][]) {
        const strut = new THREE.Mesh(
          new THREE.BoxGeometry(8, 0.6, 1.2),
          new THREE.MeshStandardMaterial({ color: 0x5a1010, metalness: 0.8, roughness: 0.2, emissive: 0x1a0000 })
        );
        strut.position.set(-1, side * 0, zOff * 1.5);
        strut.rotation.y = side * 0.15;
        group.add(strut);

        // Engine pod at wing tip
        const pod = new THREE.Mesh(
          new THREE.CylinderGeometry(0.5, 0.7, 3, 6),
          new THREE.MeshStandardMaterial({ color: 0x3a0808, metalness: 0.9, roughness: 0.15, emissive: 0x330000 })
        );
        pod.rotation.z = Math.PI / 2;
        pod.position.set(-4.5, 0, zOff * 1.5);
        group.add(pod);

        // Engine glow (emissive disk at back of pod)
        const glow = new THREE.Mesh(
          new THREE.CylinderGeometry(0.45, 0.45, 0.3, 12),
          new THREE.MeshBasicMaterial({ color: 0xff2200, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending })
        );
        glow.rotation.z = Math.PI / 2;
        glow.position.set(-6.2, 0, zOff * 1.5);
        glow.name = `engine_glow_${side > 0 ? "a" : "b"}`;
        group.add(glow);
      }

      // Running lights
      const light = new THREE.PointLight(0xff3300, 3, 20);
      light.name = "engine_light";
      light.position.set(-6, 0, 0);
      group.add(light);

    } else {
      // Coalition — classic saucer + nacelles
      // Saucer — LatheGeometry for smooth profile
      const saucerPoints: THREE.Vector2[] = [];
      for (let i = 0; i <= 20; i++) {
        const t = i / 20;
        const r = Math.sin(t * Math.PI) * 4.8;
        const y = (t - 0.5) * 1.2;
        saucerPoints.push(new THREE.Vector2(r, y));
      }
      const saucer = new THREE.Mesh(
        new THREE.LatheGeometry(saucerPoints, 32),
        new THREE.MeshStandardMaterial({ color: 0x8899bb, metalness: 0.55, roughness: 0.35, emissive: 0x040810 })
      );
      saucer.rotation.x = Math.PI / 2;
      saucer.position.x = 3.6;
      group.add(saucer);

      // Engineering hull
      const eng = new THREE.Mesh(
        new THREE.CylinderGeometry(1.4, 1.1, 7, 10),
        new THREE.MeshStandardMaterial({ color: 0x7788aa, metalness: 0.5, roughness: 0.4, emissive: 0x020508 })
      );
      eng.rotation.z = Math.PI / 2;
      eng.position.x = -2.4;
      group.add(eng);

      // Pylons connecting hull to nacelles
      for (const sign of [1, -1]) {
        const pylon = new THREE.Mesh(
          new THREE.BoxGeometry(3.5, 0.35, 0.5),
          new THREE.MeshStandardMaterial({ color: 0x6677aa, metalness: 0.5, roughness: 0.4 })
        );
        pylon.position.set(-1.5, 0, sign * 1.8);
        pylon.rotation.z = sign * 0.12;
        group.add(pylon);

        // Nacelles
        const nac = new THREE.Mesh(
          new THREE.CylinderGeometry(0.42, 0.42, 5.5, 10),
          new THREE.MeshStandardMaterial({ color: 0x6677aa, metalness: 0.65, roughness: 0.3, emissive: 0x010208 })
        );
        nac.rotation.z = Math.PI / 2;
        nac.position.set(-1, 0, sign * 2.8);
        group.add(nac);

        // Bussard collectors (front of nacelle) — warm glow
        const bussard = new THREE.Mesh(
          new THREE.SphereGeometry(0.45, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
          new THREE.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending })
        );
        bussard.position.set(2.5, 0, sign * 2.8);
        group.add(bussard);

        // Engine glow (back of nacelle)
        const glow = new THREE.Mesh(
          new THREE.CylinderGeometry(0.38, 0.38, 0.2, 12),
          new THREE.MeshBasicMaterial({ color: 0x4499ff, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending })
        );
        glow.rotation.z = Math.PI / 2;
        glow.position.set(-3.8, 0, sign * 2.8);
        glow.name = `engine_glow_${sign > 0 ? "a" : "b"}`;
        group.add(glow);
      }

      const engineLight = new THREE.PointLight(0x4488ff, 2.5, 18);
      engineLight.name = "engine_light";
      engineLight.position.set(-4, 0, 0);
      group.add(engineLight);
    }

    // Shield bubble — fresnel shader
    const shieldColor = isVelan ? new THREE.Color(0xff4444) : new THREE.Color(0x4488ff);
    const shield = new THREE.Mesh(
      new THREE.SphereGeometry(9, 24, 24),
      new THREE.ShaderMaterial({
        uniforms: {
          color: { value: shieldColor },
          pulse: { value: 0.0 },
        },
        vertexShader: `
          varying vec3 vNormal;
          varying vec3 vViewDir;
          void main() {
            vNormal = normalize(normalMatrix * normal);
            vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
            vViewDir = normalize(-mvPos.xyz);
            gl_Position = projectionMatrix * mvPos;
          }
        `,
        fragmentShader: `
          uniform vec3 color;
          uniform float pulse;
          varying vec3 vNormal;
          varying vec3 vViewDir;
          void main() {
            float fresnel = pow(1.0 - max(dot(vNormal, vViewDir), 0.0), 2.5);
            float alpha = fresnel * (0.5 + pulse * 0.3);
            gl_FragColor = vec4(color, alpha);
          }
        `,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending,
      })
    );
    shield.name = "shield";
    shield.visible = false;
    group.add(shield);

    return group;
  }

  if (type === "star") {
    const sd = entity.starData;
    const color = sd ? new THREE.Color(sd.color) : new THREE.Color(0xfff4e0);
    const radius = sd?.radius ?? 50;

    // Core sphere — emissive, no lighting dependency
    const core = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 32, 32),
      new THREE.MeshBasicMaterial({ color, fog: false })
    );
    group.add(core);

    // Point light — the primary light source in the system
    const luminosity = sd?.luminosity ?? 1.0;
    const light = new THREE.PointLight(color, luminosity * 12, 0, 0.6);
    light.name = "star_light";
    group.add(light);

    return group;
  }

  if (type === "jump_point") {
    // Ring gate structure
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(8, 1.2, 16, 32),
      new THREE.MeshStandardMaterial({
        color: 0x4488cc,
        emissive: 0x224466,
        metalness: 0.8,
        roughness: 0.2,
      })
    );
    group.add(ring);

    // Inner energy glow
    const innerGlow = new THREE.Mesh(
      new THREE.TorusGeometry(8, 0.5, 8, 32),
      new THREE.MeshBasicMaterial({
        color: 0x66ccff,
        transparent: true,
        opacity: 0.3,
        blending: THREE.AdditiveBlending,
      })
    );
    innerGlow.name = "gate_glow";
    group.add(innerGlow);

    // Point light
    const gateLight = new THREE.PointLight(0x4488cc, 1, 40);
    group.add(gateLight);

    return group;
  }

  // Generic fallback (station, shuttle, debris)
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(1.5, 1.5, 9, 8),
    new THREE.MeshStandardMaterial({ color: 0x888888, metalness: 0.4, roughness: 0.5 })
  );
  body.rotation.z = Math.PI / 2;
  group.add(body);
  return group;
}

// ── Effect mesh factory ──

function createEffectMesh(effect: { type: string; from?: any; to?: any; position?: any; color?: string; size?: number; duration: number }): THREE.Group | null {
  const group = new THREE.Group();

  if (effect.type === "beam" && effect.from && effect.to) {
    const from = new THREE.Vector3(effect.from.x, effect.from.y, effect.from.z);
    const to   = new THREE.Vector3(effect.to.x, effect.to.y, effect.to.z);
    const mid  = from.clone().add(to).multiplyScalar(0.5);
    const len  = from.distanceTo(to);
    const dir  = to.clone().sub(from).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);

    const color = new THREE.Color(effect.color || "#4488ff");

    // Core beam
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.12, len, 6),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending })
    );
    beam.position.copy(mid);
    beam.quaternion.copy(quat);
    group.add(beam);

    // Glow sheath
    const glow = new THREE.Mesh(
      new THREE.CylinderGeometry(0.45, 0.45, len, 6),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    glow.position.copy(mid);
    glow.quaternion.copy(quat);
    group.add(glow);
    return group;
  }

  if (effect.type === "explosion" && effect.position) {
    const col = new THREE.Color(effect.color || "#ff8800");
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(effect.size || 3, 16, 16),
      new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending })
    );
    ball.position.set(effect.position.x, effect.position.y, effect.position.z);
    ball.add(new THREE.PointLight(col.getHex(), 30, (effect.size || 3) * 12));

    // Shockwave ring
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(effect.size || 3, 0.4, 8, 32),
      new THREE.MeshBasicMaterial({ color: 0xffaa44, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    ring.position.set(effect.position.x, effect.position.y, effect.position.z);
    group.add(ball);
    group.add(ring);
    group.userData.ringRef = ring;
    group.userData.ballRef = ball;
    group.userData.startTime = performance.now();
    group.userData.duration = effect.duration;
    return group;
  }

  if (effect.type === "shield_impact" && effect.position) {
    const col = new THREE.Color(effect.color || "#4488ff");
    const impact = new THREE.Mesh(
      new THREE.SphereGeometry(5, 16, 12, 0, Math.PI),
      new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.7, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    impact.position.set(effect.position.x, effect.position.y, effect.position.z);
    impact.add(new THREE.PointLight(col.getHex(), 8, 25));
    group.add(impact);
    group.userData.impactRef = impact;
    group.userData.startTime = performance.now();
    group.userData.duration = effect.duration;
    return group;
  }

  // Scan: expanding ring pulses emanating from the source toward the target
  if (effect.type === "scan" && effect.from && effect.to) {
    const from = new THREE.Vector3(effect.from.x, effect.from.y, effect.from.z);
    const to   = new THREE.Vector3(effect.to.x, effect.to.y, effect.to.z);
    const dir  = to.clone().sub(from).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);

    // Create 3 staggered ring pulses
    const rings: THREE.Mesh[] = [];
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(1.5, 2.5, 32),
        new THREE.MeshBasicMaterial({ color: 0x44ccff, transparent: true, opacity: 0.4, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      ring.position.copy(from);
      ring.quaternion.copy(quat);
      group.add(ring);
      rings.push(ring);
    }

    group.userData.scanRings = rings;
    group.userData.scanFrom = from;
    group.userData.scanTo = to;
    group.userData.scanDir = dir;
    group.userData.scanDist = from.distanceTo(to);
    group.userData.startTime = performance.now();
    group.userData.duration = effect.duration;
    return group;
  }

  // Tractor beam: steady cylinder connecting source to target
  if (effect.type === "tractor" && effect.from && effect.to) {
    const from = new THREE.Vector3(effect.from.x, effect.from.y, effect.from.z);
    const to   = new THREE.Vector3(effect.to.x, effect.to.y, effect.to.z);
    const mid  = from.clone().add(to).multiplyScalar(0.5);
    const len  = from.distanceTo(to);
    const dir  = to.clone().sub(from).normalize();
    const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.3, 0.3, len, 8),
      new THREE.MeshBasicMaterial({ color: 0x88ccff, transparent: true, opacity: 0.25, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    beam.position.copy(mid);
    beam.quaternion.copy(quat);
    group.add(beam);
    return group;
  }

  if (effect.type === "torpedo" && effect.from && effect.to) {
    const col = new THREE.Color(effect.color || "#ff6600");
    const ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.4, 8, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff, blending: THREE.AdditiveBlending })
    );
    ball.add(new THREE.PointLight(col.getHex(), 8, 12));

    // Glow corona
    const corona = new THREE.Mesh(
      new THREE.SphereGeometry(0.9, 8, 8),
      new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    ball.add(corona);

    ball.position.set(effect.from.x, effect.from.y, effect.from.z);
    group.add(ball);
    group.userData.torpedoFrom   = effect.from;
    group.userData.torpedoTo     = effect.to;
    group.userData.torpedoStart  = performance.now() / 1000;
    group.userData.torpedoDuration = effect.duration;
    return group;
  }

  if (effect.type === "transporter" && effect.position) {
    // Vertical column of shimmering particles
    const count = 80;
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * 2;
      positions[i * 3 + 1] = Math.random() * 8 - 2;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2;
    }
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pts = new THREE.Points(geo, new THREE.PointsMaterial({
      color: 0x88ccff,
      size: 0.3,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    pts.position.set(effect.position.x, effect.position.y, effect.position.z);
    group.add(pts);
    // Light column
    const light = new THREE.PointLight(0x88ccff, 12, 20);
    light.position.set(effect.position.x, effect.position.y + 3, effect.position.z);
    group.add(light);
    group.userData.transporterRef = pts;
    group.userData.startTime = performance.now();
    group.userData.duration = effect.duration;
    return group;
  }

  if (effect.type === "cloak" && effect.position) {
    // Shimmering sphere that expands and fades
    const sphere = new THREE.Mesh(
      new THREE.SphereGeometry(effect.size || 8, 16, 12),
      new THREE.MeshBasicMaterial({
        color: 0x4488aa,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        wireframe: true,
      })
    );
    sphere.position.set(effect.position.x, effect.position.y, effect.position.z);
    group.add(sphere);
    group.userData.cloakRef = sphere;
    group.userData.startTime = performance.now();
    group.userData.duration = effect.duration;
    return group;
  }

  return null;
}

// ── Starfield with spectral colors ──

function createStarfield(): THREE.Points {
  const COUNT = 3000;
  const geo   = new THREE.BufferGeometry();
  const pos   = new Float32Array(COUNT * 3);
  const col   = new Float32Array(COUNT * 3);
  const sizes = new Float32Array(COUNT);
  const phases = new Float32Array(COUNT);  // twinkling phase offset
  const speeds = new Float32Array(COUNT);  // twinkling speed

  for (let i = 0; i < COUNT; i++) {
    const r     = 5000 + Math.random() * 3000;
    const theta = Math.random() * Math.PI * 2;
    const phi   = Math.acos(2 * Math.random() - 1);
    pos[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
    pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
    pos[i * 3 + 2] = r * Math.cos(phi);

    // Spectral distribution
    const rand = Math.random();
    if (rand < 0.55) {
      // White / blue-white
      col[i*3] = 0.85 + Math.random() * 0.15;
      col[i*3+1] = 0.9 + Math.random() * 0.1;
      col[i*3+2] = 1.0;
    } else if (rand < 0.75) {
      // Pure white
      const v = 0.9 + Math.random() * 0.1;
      col[i*3] = v; col[i*3+1] = v; col[i*3+2] = v;
    } else if (rand < 0.88) {
      // Yellow / yellow-white
      col[i*3] = 1.0;
      col[i*3+1] = 0.92 + Math.random() * 0.08;
      col[i*3+2] = 0.55 + Math.random() * 0.3;
    } else if (rand < 0.96) {
      // Orange
      col[i*3] = 1.0;
      col[i*3+1] = 0.5 + Math.random() * 0.25;
      col[i*3+2] = 0.15 + Math.random() * 0.2;
    } else {
      // Red giant
      col[i*3] = 1.0;
      col[i*3+1] = 0.15 + Math.random() * 0.2;
      col[i*3+2] = 0.1;
    }
    sizes[i] = Math.random() < 0.03 ? 3.5 : (Math.random() < 0.1 ? 2.0 : 1.2);
    phases[i] = Math.random() * Math.PI * 2;
    speeds[i] = 0.15 + Math.random() * 0.35;  // very slow: 0.15–0.5 Hz
  }

  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("color",    new THREE.BufferAttribute(col, 3));

  // Store base colors and twinkling params for animation
  geo.setAttribute("baseColor", new THREE.BufferAttribute(col.slice(), 3));

  const mat = new THREE.PointsMaterial({
    vertexColors: true,
    size: 1.4,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.85,
  });
  const points = new THREE.Points(geo, mat);
  points.userData.phases = phases;
  points.userData.speeds = speeds;
  return points;
}

// ── Nebula clouds ──

function createNebula(color: number, cx: number, cy: number, cz: number, radius: number, flatness: number, count: number): THREE.Points {
  const geo = new THREE.BufferGeometry();
  const pos = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r     = Math.pow(Math.random(), 0.5) * radius;
    const theta = Math.random() * Math.PI * 2;
    const phi   = Math.acos(2 * Math.random() - 1);
    pos[i*3]   = cx + r * Math.sin(phi) * Math.cos(theta);
    pos[i*3+1] = cy + r * Math.sin(phi) * Math.sin(theta) * flatness;
    pos[i*3+2] = cz + r * Math.cos(phi);
  }
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  return new THREE.Points(geo, new THREE.PointsMaterial({
    color,
    size: 2.0,
    sizeAttenuation: false,
    transparent: true,
    opacity: 0.07,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexColors: false,
  }));
}

// ── Trail system ──

interface Trail {
  positions: Float32Array;
  geometry: THREE.BufferGeometry;
  points: THREE.Points;
  frameCount: number;
  prevPos: THREE.Vector3;
}

function createTrail(initialPos: THREE.Vector3, color: number): Trail {
  const positions = new Float32Array(TRAIL_LENGTH * 3);
  for (let i = 0; i < TRAIL_LENGTH; i++) {
    positions[i*3]   = initialPos.x;
    positions[i*3+1] = initialPos.y;
    positions[i*3+2] = initialPos.z;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color,
    size: 0.7,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.55,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const points = new THREE.Points(geo, mat);
  return { positions, geometry: geo, points, frameCount: 0, prevPos: initialPos.clone() };
}

// ── Main component ──

export function Viewport3D({ alertLevel, executor, onReady, replaySpatial, replayEffects, cameraOverride }: Props) {
  const canvasRef      = useRef<HTMLCanvasElement>(null);
  const containerRef   = useRef<HTMLDivElement>(null);
  const warpOverlayRef = useRef<HTMLDivElement>(null);
  const executorRef    = useRef(executor);
  executorRef.current  = executor;
  const onReadyRef     = useRef(onReady);
  onReadyRef.current   = onReady;
  const replaySpatialRef = useRef(replaySpatial);
  replaySpatialRef.current = replaySpatial;
  const replayEffectsRef = useRef(replayEffects);
  replayEffectsRef.current = replayEffects;
  const cameraOverrideRef = useRef(cameraOverride);
  cameraOverrideRef.current = cameraOverride;

  const internalsRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    composer: EffectComposer;
    entities: Map<string, THREE.Group>;
    trails: Map<string, Trail>;
    ambientLight: THREE.AmbientLight;
    dirLight: THREE.DirectionalLight;
    animId: number;
  } | null>(null);

  useEffect(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // ── Scene setup ──
    const scene  = new THREE.Scene();
    scene.fog    = new THREE.FogExp2(0x020408, 0.00008);

    const camera = new THREE.PerspectiveCamera(60, container.clientWidth / container.clientHeight, 0.1, 5000);
    camera.position.set(0, 2, 0);
    camera.lookAt(100, 0, 0);

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000);
    renderer.toneMapping = THREE.ReinhardToneMapping;
    renderer.toneMappingExposure = 0.9;

    // ── Bloom postprocessing ──
    const composer   = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
    const bloomPass  = new UnrealBloomPass(
      new THREE.Vector2(container.clientWidth / 2, container.clientHeight / 2),
      0.45,  // strength
      0.3,   // radius
      0.3    // threshold
    );
    composer.addPass(bloomPass);

    // ── Lighting ──
    // Low ambient — keeps shadow sides from going pure black
    const ambientLight = new THREE.AmbientLight(0x080810, 0.3);
    scene.add(ambientLight);

    // Subtle fill light for rim definition
    const fillLight = new THREE.DirectionalLight(0x334466, 0.3);
    fillLight.position.set(400, -60, -200);
    scene.add(fillLight);

    // ── Starfield ──
    const stars = createStarfield();
    scene.add(stars);

    // ── Nebula clouds — placed far away so they only show as background color ──
    scene.add(createNebula(0x6622aa, -1800, 400, -2400, 900, 0.03, 150));
    scene.add(createNebula(0x004466, 2400, -200, -1600, 800, 0.02, 100));
    scene.add(createNebula(0x662200, -800, -400, 2800, 1000, 0.04, 120));

    // ── Resize ──
    const onResize = () => {
      const w = container.clientWidth, h = container.clientHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
      bloomPass.resolution.set(w / 2, h / 2);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(container);

    // ── Per-frame state ──
    let animId    = 0;
    let lastTime  = 0;
    let firstFrame = true;
    const entityMap = new Map<string, THREE.Group>();
    const trailMap  = new Map<string, Trail>();

    // Camera banking state
    let prevHeading  = { x: 1, y: 0, z: 0 };
    let bankAngle    = 0;
    let warpWasActive = false;

    const animate = (time: number) => {
      animId   = requestAnimationFrame(animate);
      const delta = lastTime ? Math.min((time - lastTime) / 1000, 0.1) : 0.016;
      lastTime = time;

      stars.rotation.y += delta * 0.0003;

      // Slow star twinkling
      const starGeo = stars.geometry;
      const colorAttr = starGeo.getAttribute("color") as THREE.BufferAttribute;
      const baseColor = starGeo.getAttribute("baseColor") as THREE.BufferAttribute;
      const { phases: ph, speeds: sp } = stars.userData;
      const t = time * 0.001;  // seconds
      for (let i = 0; i < colorAttr.count; i++) {
        const brightness = 0.6 + 0.4 * Math.sin(t * sp[i] + ph[i]);
        colorAttr.setXYZ(
          i,
          baseColor.getX(i) * brightness,
          baseColor.getY(i) * brightness,
          baseColor.getZ(i) * brightness,
        );
      }
      colorAttr.needsUpdate = true;

      // Determine spatial state source: replay or live executor
      const replaySp = replaySpatialRef.current;
      const spatial = replaySp ?? executorRef.current?.currentSpatialState;

      if (spatial) {
        const pp = spatial.playerShip.position;
        const hd = spatial.playerShip.heading;
        const heading3 = new THREE.Vector3(hd.x, hd.y, hd.z).normalize();

        // ── Camera: replay override or normal modes ──
        if (cameraOverrideRef.current) {
          cameraOverrideRef.current(camera, delta);
        } else {

        // ── Camera transition progress ──
        if (cameraState.transitionProgress < 1) {
          cameraState.transitionProgress = Math.min(1, cameraState.transitionProgress + delta * 1.5);
        }
        const camT = cameraState.transitionProgress * cameraState.transitionProgress * (3 - 2 * cameraState.transitionProgress); // smooth hermite

        // ── Camera modes ──
        const worldUp  = new THREE.Vector3(0, 1, 0);

        if (cameraState.mode === "tactical") {
          // Top-down angled view centered on player
          const tacticalPos = new THREE.Vector3(pp.x - 30, pp.y + 120, pp.z);
          const tacticalLook = new THREE.Vector3(pp.x + 20, pp.y, pp.z);
          camera.position.lerp(tacticalPos, 0.03 + camT * 0.05);
          camera.up.lerp(worldUp, 0.1);
          camera.lookAt(tacticalLook);
          camera.fov = THREE.MathUtils.lerp(camera.fov, 60, 0.05);
          camera.updateProjectionMatrix();
          bankAngle = THREE.MathUtils.lerp(bankAngle, 0, 0.1);
        } else if (cameraState.mode === "magnify") {
          // Telephoto zoom toward target entity
          const targetEntity = cameraState.targetId
            ? spatial.entities.find(e => e.id === cameraState.targetId)
            : undefined;
          if (targetEntity) {
            const targetPos = new THREE.Vector3(targetEntity.position.x, targetEntity.position.y, targetEntity.position.z);
            const camPos = new THREE.Vector3(pp.x, pp.y + 1.5, pp.z);
            camera.position.lerp(camPos, 0.06);
            camera.up.lerp(worldUp, 0.1);
            camera.lookAt(targetPos);
            camera.fov = THREE.MathUtils.lerp(camera.fov, 18, 0.04 + camT * 0.04);
            camera.updateProjectionMatrix();
          }
          bankAngle = THREE.MathUtils.lerp(bankAngle, 0, 0.1);
        } else {
          // Default follow camera with banking
          // Turn rate → bank angle
          const prevH = new THREE.Vector3(prevHeading.x, prevHeading.y, prevHeading.z);
          const crossY = prevH.clone().cross(heading3).y;
          const targetBank = THREE.MathUtils.clamp(-crossY * 80, -0.28, 0.28);
          bankAngle = THREE.MathUtils.lerp(bankAngle, targetBank, 0.04);

          // Apply banking via camera.up
          const bankQuat = new THREE.Quaternion().setFromAxisAngle(heading3, bankAngle);
          camera.up.copy(worldUp.clone().applyQuaternion(bankQuat));

          const camTarget = new THREE.Vector3(pp.x, pp.y + 1.5, pp.z);
          camera.position.lerp(camTarget, 0.06);
          camera.lookAt(new THREE.Vector3(
            pp.x + hd.x * 100,
            pp.y + hd.y * 100,
            pp.z + hd.z * 100,
          ));

          // Restore FOV from magnify
          if (camera.fov < 59) {
            camera.fov = THREE.MathUtils.lerp(camera.fov, 60, 0.05);
            camera.updateProjectionMatrix();
          }
        }
        prevHeading = { x: hd.x, y: hd.y, z: hd.z };

        } // end camera override else

        // ── Entity sync ──
        const activeIds = new Set<string>(["player_ship"]);
        for (const entity of spatial.entities) {
          activeIds.add(entity.id);
          let group = entityMap.get(entity.id);
          if (!group) {
            group = createEntityMesh(entity);
            scene.add(group);
            entityMap.set(entity.id, group);
          }
          group.position.set(entity.position.x, entity.position.y, entity.position.z);

          // Cloak visibility — cloaked ships shimmer faintly
          const isCloaked = (entity as any).cloaked === true;
          if (entity.type === "ship") {
            group.traverse(child => {
              if ((child as THREE.Mesh).material && child !== group.userData.shieldMesh) {
                const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
                if (mat.opacity !== undefined) {
                  mat.transparent = true;
                  mat.opacity = isCloaked ? 0.08 + Math.sin(time * 0.005) * 0.04 : 1;
                }
              }
            });
          }

          // Rotate planet body (use userData refs to avoid traverse)
          if (group.userData.bodyMesh) {
            const rotSpeed = group.userData.planetConfig?.rotationSpeed ?? 0.003;
            (group.userData.bodyMesh as THREE.Mesh).rotation.y += delta * rotSpeed;
          }
          const clouds = group.userData.cloudMesh as THREE.Mesh | undefined
            ?? (group.getObjectByName("clouds") as THREE.Mesh | undefined);
          if (clouds) {
            group.userData.cloudMesh = clouds;
            const rotSpeed = group.userData.planetConfig?.rotationSpeed ?? 0.003;
            clouds.rotation.y += delta * (rotSpeed * 1.6);
          }

          // Shield fresnel pulse (cache lookup in userData)
          const shield = (group.userData.shieldMesh as THREE.Mesh | undefined)
            ?? (group.getObjectByName("shield") as THREE.Mesh | undefined);
          if (shield) {
            group.userData.shieldMesh = shield;
            shield.visible = entity.shields === "up";
            if (shield.visible) {
              const mat = shield.material as THREE.ShaderMaterial;
              if (mat.uniforms) mat.uniforms.pulse.value = (Math.sin(time * 0.003) + 1) * 0.5;
            }
          }

          // Damage spark particles
          if (entity.type === "ship" && entity.condition && entity.condition !== "healthy") {
            const severity = entity.condition === "critical" ? 3 : entity.condition === "badly_damaged" ? 2 : 1;
            const sparkCount = severity * 5;
            let sparks = group.userData.sparks as THREE.Points | undefined;
            if (!sparks || (group.userData.sparkCount as number) !== sparkCount) {
              if (sparks) { group.remove(sparks); sparks.geometry.dispose(); }
              const geo = new THREE.BufferGeometry();
              const pos = new Float32Array(sparkCount * 3);
              geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
              sparks = new THREE.Points(geo, new THREE.PointsMaterial({
                color: severity >= 3 ? 0xff2200 : 0xff7700,
                size: severity >= 2 ? 0.5 : 0.35,
                sizeAttenuation: true,
                transparent: true,
                opacity: 0.9,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
              }));
              group.add(sparks);
              group.userData.sparks      = sparks;
              group.userData.sparkCount  = sparkCount;
              group.userData.sparkLife   = new Float32Array(sparkCount).map(() => Math.random());
              group.userData.sparkVelY   = new Float32Array(sparkCount).map(() => 0.02 + Math.random() * 0.04);
              group.userData.sparkVelXZ  = new Float32Array(sparkCount * 2).map(() => (Math.random() - 0.5) * 0.05);
              const sp = geo.getAttribute("position") as THREE.BufferAttribute;
              for (let k = 0; k < sparkCount; k++) {
                sp.setXYZ(k, (Math.random()-0.5)*6, (Math.random()-0.5)*2, (Math.random()-0.5)*6);
              }
              sp.needsUpdate = true;
            }
            // Animate sparks
            const sp  = sparks.geometry.getAttribute("position") as THREE.BufferAttribute;
            const life = group.userData.sparkLife as Float32Array;
            const velY = group.userData.sparkVelY  as Float32Array;
            const velXZ= group.userData.sparkVelXZ as Float32Array;
            for (let k = 0; k < sparkCount; k++) {
              life[k] -= delta * (1.5 + Math.random() * 0.5);
              if (life[k] <= 0) {
                life[k] = 0.6 + Math.random() * 0.6;
                sp.setXYZ(k, (Math.random()-0.5)*7, (Math.random()-0.5)*2, (Math.random()-0.5)*7);
                velY[k]       = 0.02 + Math.random() * 0.04;
                velXZ[k*2]    = (Math.random()-0.5)*0.06;
                velXZ[k*2+1]  = (Math.random()-0.5)*0.06;
              } else {
                sp.setXYZ(k, sp.getX(k) + velXZ[k*2], sp.getY(k) + velY[k], sp.getZ(k) + velXZ[k*2+1]);
              }
            }
            sp.needsUpdate = true;
          } else if (group.userData.sparks) {
            group.remove(group.userData.sparks as THREE.Points);
            (group.userData.sparks as THREE.Points).geometry.dispose();
            delete group.userData.sparks;
            delete group.userData.sparkCount;
          }
        }

        // Remove despawned entities
        for (const [id, group] of entityMap) {
          if (!activeIds.has(id) && !id.startsWith("fx_")) {
            scene.remove(group);
            entityMap.delete(id);
            const trail = trailMap.get(id);
            if (trail) { scene.remove(trail.points); trailMap.delete(id); }
          }
        }

        // ── Trail + engine glow for all ships ──
        const shipEntries: Array<{ id: string; pos: THREE.Vector3; heading: THREE.Vector3; faction?: string }> = [
          {
            id: "player_ship",
            pos: new THREE.Vector3(pp.x, pp.y, pp.z),
            heading: heading3,
            faction: "Stellar Coalition",
          },
          ...spatial.entities
            .filter(e => e.type === "ship")
            .map(e => ({
              id: e.id,
              pos: new THREE.Vector3(e.position.x, e.position.y, e.position.z),
              heading: new THREE.Vector3(1, 0, 0), // approximate; updated below
              faction: e.faction,
            })),
        ];

        for (const ship of shipEntries) {
          const isVelan = ship.faction === "Velan Dominion";
          const trailColor = isVelan ? 0xff3300 : 0x4499ff;

          // Init trail if first time
          let trail = trailMap.get(ship.id);
          if (!trail) {
            trail = createTrail(ship.pos, trailColor);
            scene.add(trail.points);
            trailMap.set(ship.id, trail);
          }

          // Compute velocity from position delta
          const dx = ship.pos.x - trail.prevPos.x;
          const dy = ship.pos.y - trail.prevPos.y;
          const dz = ship.pos.z - trail.prevPos.z;
          const speed = Math.sqrt(dx*dx + dy*dy + dz*dz);
          const isMoving = speed > 0.005;

          if (isMoving && ship.id !== "player_ship") {
            // Approximate heading from movement direction
            ship.heading.set(dx / speed, dy / speed, dz / speed);
          }

          if (isMoving) {
            trail.frameCount++;
            if (trail.frameCount % TRAIL_UPDATE_FRAMES === 0) {
              // Shift positions back and add new at front
              const pa = trail.positions;
              for (let i = TRAIL_LENGTH - 1; i > 0; i--) {
                pa[i*3]   = pa[(i-1)*3];
                pa[i*3+1] = pa[(i-1)*3+1];
                pa[i*3+2] = pa[(i-1)*3+2];
              }
              // Offset trail origin behind engines
              pa[0] = ship.pos.x - ship.heading.x * 4;
              pa[1] = ship.pos.y - ship.heading.y * 4;
              pa[2] = ship.pos.z - ship.heading.z * 4;
              trail.geometry.attributes.position.needsUpdate = true;
            }
          }

          trail.prevPos.copy(ship.pos);

          // Engine glow — scale with speed
          const entityGroup = entityMap.get(ship.id);
          if (entityGroup) {
            if (!entityGroup.userData.glowA) {
              entityGroup.userData.glowA = entityGroup.getObjectByName("engine_glow_a");
              entityGroup.userData.glowB = entityGroup.getObjectByName("engine_glow_b");
              entityGroup.userData.engLight = entityGroup.getObjectByName("engine_light");
            }
            const glowA = entityGroup.userData.glowA as THREE.Mesh | undefined;
            const glowB = entityGroup.userData.glowB as THREE.Mesh | undefined;
            const engLight = entityGroup.userData.engLight as THREE.PointLight | undefined;
            const glowScale = isMoving ? 1.0 + speed * 0.8 : 0.6;
            const glowOpacity = isMoving ? 0.9 : 0.4;
            for (const g of [glowA, glowB]) {
              if (g) {
                const mat = g.material as THREE.MeshBasicMaterial;
                mat.opacity = glowOpacity;
                g.scale.setScalar(glowScale);
              }
            }
            if (engLight) engLight.intensity = isMoving ? 3 + speed * 0.5 : 1.5;
          }
        }

        // ── Effects ──
        const effectsSource = replayEffectsRef.current ?? executorRef.current?.effects ?? [];
        for (const effect of effectsSource) {
          if (entityMap.has("fx_" + effect.id)) continue;
          const mesh = createEffectMesh(effect);
          if (mesh) {
            scene.add(mesh);
            entityMap.set("fx_" + effect.id, mesh);
            setTimeout(() => {
              scene.remove(mesh);
              entityMap.delete("fx_" + effect.id);
              if (!replayEffectsRef.current) {
                executorRef.current?.removeEffect(effect.id);
              }
            }, effect.duration * 1000);
          }
        }

        // In replay mode, remove effect meshes no longer in the active list
        if (replayEffectsRef.current) {
          const activeIds = new Set(replayEffectsRef.current.map(e => "fx_" + e.id));
          for (const [id, group] of entityMap) {
            if (id.startsWith("fx_") && !activeIds.has(id)) {
              scene.remove(group);
              entityMap.delete(id);
            }
          }
        }
      }

      // ── Animate torpedoes & effects ──
      for (const [id, group] of entityMap) {
        if (!id.startsWith("fx_")) continue;

        // Torpedo movement
        if (group.userData.torpedoTo) {
          const now = performance.now() / 1000;
          const t   = Math.min((now - group.userData.torpedoStart) / group.userData.torpedoDuration, 1);
          const from = group.userData.torpedoFrom;
          const to   = group.userData.torpedoTo;
          const child = group.children[0];
          if (child) {
            child.position.set(
              from.x + (to.x - from.x) * t,
              from.y + (to.y - from.y) * t,
              from.z + (to.z - from.z) * t,
            );
          }
        }

        // Scan ring pulses — rings expand and travel from source to target
        if (group.userData.scanRings) {
          const elapsed = (performance.now() - group.userData.startTime) / 1000;
          const duration = group.userData.duration;
          const rings = group.userData.scanRings as THREE.Mesh[];
          const from = group.userData.scanFrom as THREE.Vector3;
          const dir = group.userData.scanDir as THREE.Vector3;
          const dist = group.userData.scanDist as number;

          for (let i = 0; i < rings.length; i++) {
            // Each ring starts at a staggered time, cycles repeatedly
            const cycleTime = duration / 2; // rings repeat during the scan
            const offset = (i / rings.length) * cycleTime;
            const ringElapsed = ((elapsed - offset) % cycleTime + cycleTime) % cycleTime;
            const ringProgress = ringElapsed / cycleTime;

            // Travel from source toward target
            const travelDist = ringProgress * dist;
            rings[i].position.copy(from.clone().add(dir.clone().multiplyScalar(travelDist)));

            // Expand as they travel
            const scale = 1 + ringProgress * 4;
            rings[i].scale.setScalar(scale);

            // Fade out as they travel further
            (rings[i].material as THREE.MeshBasicMaterial).opacity = 0.4 * (1 - ringProgress * 0.8);
          }
        }

        // Explosion expansion
        if (group.userData.ballRef) {
          const elapsed  = (performance.now() - group.userData.startTime) / 1000;
          const progress = Math.min(elapsed / group.userData.duration, 1);
          const ball = group.userData.ballRef as THREE.Mesh;
          const ring = group.userData.ringRef as THREE.Mesh;
          ball.scale.setScalar(1 + progress * 3);
          (ball.material as THREE.MeshBasicMaterial).opacity = 0.9 * (1 - progress);
          if (ring) {
            ring.scale.setScalar(1 + progress * 5);
            (ring.material as THREE.MeshBasicMaterial).opacity = 0.6 * (1 - progress);
          }
        }

        // Shield impact shrink
        if (group.userData.impactRef) {
          const elapsed  = (performance.now() - group.userData.startTime) / 1000;
          const progress = Math.min(elapsed / group.userData.duration, 1);
          const impact = group.userData.impactRef as THREE.Mesh;
          impact.scale.setScalar(1 + progress * 1.5);
          (impact.material as THREE.MeshBasicMaterial).opacity = 0.7 * (1 - progress);
        }

        // Transporter shimmer
        if (group.userData.transporterRef) {
          const elapsed  = (performance.now() - group.userData.startTime) / 1000;
          const progress = Math.min(elapsed / group.userData.duration, 1);
          const pts = group.userData.transporterRef as THREE.Points;
          const mat = pts.material as THREE.PointsMaterial;
          // Fade in then out
          mat.opacity = progress < 0.3 ? progress / 0.3 : Math.max(0, 1 - (progress - 0.3) / 0.7);
          // Shimmer the particle positions upward
          const geo = pts.geometry.getAttribute("position") as THREE.BufferAttribute;
          for (let k = 0; k < geo.count; k++) {
            geo.setY(k, geo.getY(k) + delta * (1 + Math.random() * 2));
            if (geo.getY(k) > 6) geo.setY(k, -2);
          }
          geo.needsUpdate = true;
        }

        // Cloak shimmer — expand wireframe sphere then fade
        if (group.userData.cloakRef) {
          const elapsed  = (performance.now() - group.userData.startTime) / 1000;
          const progress = Math.min(elapsed / group.userData.duration, 1);
          const sphere = group.userData.cloakRef as THREE.Mesh;
          sphere.scale.setScalar(0.5 + progress * 1.5);
          sphere.rotation.y += delta * 2;
          (sphere.material as THREE.MeshBasicMaterial).opacity = 0.5 * (1 - progress);
        }
      }

      // ── Warp flash — star elongation + DOM overlay ──
      if (warpState.active) {
        warpState.elapsed += delta;
        if (warpState.elapsed >= warpState.duration) {
          warpState.active = false;
          warpWasActive    = false;
          (stars.material as THREE.PointsMaterial).size = 1.4;
        } else {
          const t    = warpState.elapsed / warpState.duration;
          const peak = t < 0.35 ? t / 0.35 : Math.max(0, 1 - (t - 0.35) / 0.65);
          (stars.material as THREE.PointsMaterial).size = 1.4 + peak * 22;
        }
        if (!warpWasActive && warpOverlayRef.current) {
          warpOverlayRef.current.classList.add("warp-flash-active");
          const el = warpOverlayRef.current;
          setTimeout(() => el.classList.remove("warp-flash-active"), 1600);
          warpWasActive = true;
        }
      }

      // ── Bridge shake ──
      if (shakeState.active) {
        shakeState.elapsed += delta;
        if (shakeState.elapsed >= shakeState.duration) {
          shakeState.active = false;
        } else {
          const t   = shakeState.elapsed / shakeState.duration;
          const amp = shakeState.intensity * (1 - t);
          camera.position.x += Math.sin(shakeState.elapsed * 22) * amp * 0.5;
          camera.position.y += Math.cos(shakeState.elapsed * 28) * amp * 0.35;
        }
      }

      composer.render();

      if (firstFrame) {
        firstFrame = false;
        onReadyRef.current?.();
      }
    };

    animate(0);
    internalsRef.current = { scene, camera, renderer, composer, entities: entityMap, trails: trailMap, ambientLight, dirLight: fillLight, animId };

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      renderer.dispose();
      internalsRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Alert lighting ──
  useEffect(() => {
    if (!internalsRef.current) return;
    const c = ALERT_COLORS[alertLevel];
    internalsRef.current.ambientLight.color.setHex(c.ambient);
    internalsRef.current.dirLight.color.setHex(c.dir);
    // Tighten bloom at red alert
    const composer = internalsRef.current.composer;
    const bloom    = composer.passes[1] as UnrealBloomPass;
    if (bloom) bloom.strength = alertLevel === "red" ? 0.7 : alertLevel === "yellow" ? 0.55 : 0.45;
  }, [alertLevel]);

  return (
    <div className="viewport-3d" ref={containerRef}>
      <canvas ref={canvasRef} />
      <div className="warp-flash-overlay" ref={warpOverlayRef} />
    </div>
  );
}
